import { getApplicationVersion } from "@/services/api";
import { configValue } from "@/services/config";
import posthog from "posthog-js";
import { identifyPostHogUser, isPostHogEnabled, resetPostHog, setPostHogCompany } from "@/services/posthog";
import { intercomBoot, intercomShutdown } from "@/services/intercom";
import { applicationInsightsInstance } from "@/services/application-insights/application-insights";
import { trackUnknownError } from "@/services/application-insights/track-unknown-error";
import type { ToastArgs } from "@/components/Toaster/types";
import { defineStore } from "pinia";
import axios from "axios";
import type { CompanyInformation, UserMeResponse, UserProfile } from "@/core/api/data-contracts";

export type UserState =
  | {
      isAuthenticated: true;
      roles: string[];
      profile: UserProfile;
      companies: CompanyInformation[];
      activeCompany: CompanyInformation;
    }
  | {
      isAuthenticated: false;
      roles: string[];
      profile: null;
      companies: CompanyInformation[];
      activeCompany: null;
    };

export type UserRole =
  | "se.eaztimate.hubb"
  | "se.eaztimate.hubb.arbetsledare"
  | "se.eaztimate.hubb.utforare"
  | "se.eaztimate.hubb.beta"
  | "se.eaztimate.hubb.dev"
  | "se.eaztimate.hubb.kma"
  | "se.eaztimate.hubb.csc";

export type AuthenticationState = "missing" | "initialized" | "unknown";

export type StoreState = {
  authenticationState: AuthenticationState;
  user: UserState;
  application: {
    currentVersion?: string;
    newVersion: null | string;
    hasNewVersion: boolean;
  };
};

const roles = {
  foreman: "se.eaztimate.hubb.arbetsledare",
  technician: "se.eaztimate.hubb.utforare",
  beta: "se.eaztimate.hubb.beta",
  developer: "se.eaztimate.hubb.dev",
  kma: "se.eaztimate.hubb.kma",
  customerSalesCoordinator: "se.eaztimate.hubb.csc",
} as const;

type MainRoles = "foreman" | "worker" | "customerSalesCoordinator" | "anonymous";

const anonymousUser: UserState = {
  isAuthenticated: false,
  profile: null,
  companies: [],
  roles: [],
  activeCompany: null,
} as const;

export const useAuthenticatedUserStore = defineStore("authenticatedUser", {
  state: (): StoreState => ({
    authenticationState: "missing",
    user: anonymousUser,
    application: {
      currentVersion: configValue("VUE_APP_APPLICATION_VERSION"),
      newVersion: null,
      hasNewVersion: false,
    },
  }),
  getters: {
    isForeman: (state: StoreState): boolean => isInRole(state, roles.foreman),
    isTechnician: (state: StoreState): boolean => isInRole(state, roles.technician),
    isCustomerSalesCoordinator: (state: StoreState): boolean => isInRole(state, roles.customerSalesCoordinator),
    isBetaGroup: (state: StoreState): boolean => isInRole(state, roles.beta),
    isDeveloper: (state: StoreState): boolean => isInRole(state, roles.developer),
    isKma: (state: StoreState): boolean => isInRole(state, roles.kma),
    isForemanOrAbove: (state: StoreState): boolean =>
      isInRole(state, roles.foreman) || isInRole(state, roles.customerSalesCoordinator),
    mainRole(): MainRoles {
      switch (true) {
        case this.isCustomerSalesCoordinator:
          return "customerSalesCoordinator";
        case this.isForeman:
          return "foreman";
        case this.isTechnician:
          return "worker";
        default:
          return "anonymous";
      }
    },
    userName(): string | null {
      if (this.user.profile) {
        return this.user.profile.name;
      } else {
        return null;
      }
    },
  },
  actions: {
    applicationVersion({ version }: { version: string }) {
      this.application.hasNewVersion = true;
      this.application.newVersion = version;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    toastAdd(toast: ToastArgs) {
      // NOP: This event is being handled by useToaster()
    },
    async updateVersion({ source }: { source: "user-init" | "user-update" }) {
      if (this.application.hasNewVersion) {
        return;
      }

      try {
        const { data } = await getApplicationVersion();
        if (data.version !== this.application.currentVersion) {
          this.applicationVersion({ version: data.version });
          applicationInsightsInstance().trackEvent({
            name: "Application Version Mismatch",
            properties: {
              eventCategory: "Application",
              source: source,
              currentVersion: this.application.currentVersion,
              latestVersion: data.version,
            },
          });
        }
      } catch {
        // Version tracking should not affect the login
      }
    },
    async userLogin(path: string) {
      // We're leaving the site now, so it's better to flush AI before.
      applicationInsightsInstance().flush();
      location.href = `/api/user/login?redirect=${path}`;
    },
    async userLogout() {
      resetPostHog();
      intercomShutdown();
      // We're leaving the site now, so it's better to flush AI before.
      applicationInsightsInstance().flush();
      location.href = "/api/user/logout";
    },
    setActiveCompanyById(companyId: string) {
      if (!this.user.isAuthenticated) {
        return;
      }

      if (!this.isCustomerSalesCoordinator) {
        return;
      }

      if (companyId === this.user.activeCompany?.id) {
        return;
      }

      const company = this.user.companies.find((c) => c.id === companyId);

      if (!company) {
        return;
      }

      this.user.activeCompany = company;
      setPostHogCompany(company);
      if (companyId !== this.user.profile.mainCompany.id) {
        sessionStorage.setItem(CompanyIdLocalStorageKey, companyId);
      } else {
        sessionStorage.removeItem(CompanyIdLocalStorageKey);
      }
    },
    async updateUser(userData: UserState, options: { source: "user-init" | "user-update" }) {
      this.user = userData;
      this.authenticationState = "initialized";

      if (userData.isAuthenticated) {
        setPostHogCompany(userData.activeCompany);
      } else if (isPostHogEnabled() && posthog.get_property("$user_state") === "identified") {
        // Session lost (e.g. after a 401): stop attributing events to the previous user
        resetPostHog();
      }

      if (userData.isAuthenticated && options.source === "user-init") {
        // Same data as intercomBoot below. distinct_id is the stable userId;
        // email lets support find a user when adding them to the session-replay flag.
        identifyPostHogUser(userData.profile.userId.toString(), {
          email: userData.profile.email,
          role: this.mainRole,
          is_beta_user: this.isBetaGroup,
          company_id: userData.activeCompany.id,
          company_name: userData.activeCompany.name,
        });

        intercomBoot({
          user_id: userData.profile.userId.toString(),
          email: userData.profile.email,
          mainRole: this.mainRole,
          isBetaUser: this.isBetaGroup,
          activeCompany: userData.activeCompany,
        });
      }

      await this.updateVersion(options);
    },
    async requireUser(): Promise<UserState> {
      await this.userInit();
      return this.user;
    },
    revaluateAuthenticatedUser() {
      this.authenticationState = "unknown";
    },
    async userInit() {
      // NOTE: since there are multiple async calls this call might be racy and we might need to add something like
      // https://github.com/DirtyHairy/async-mutex
      const state = this.authenticationState;
      if (state === "initialized") {
        return;
      }

      try {
        const user = await getCurrentUser();
        const preferredCompanyId = getPreferredCompanyId();

        const updatedUserState = toUserState(user, preferredCompanyId);
        await this.updateUser(updatedUserState, { source: state === "missing" ? "user-init" : "user-update" });
      } catch (e) {
        trackUnknownError(applicationInsightsInstance(), e);
        await this.userLogout();
      }
    },
  },
});

function toUserState(user: UserMeResponse, preferredCompanyId: string | null): UserState {
  if (!user.isAuthenticated || !user.profile) {
    return anonymousUser;
  }

  const preferredCompany = preferredCompanyId
    ? user.companies.find((company) => company.id === preferredCompanyId)
    : null;

  return {
    isAuthenticated: true,
    profile: user.profile,
    roles: user.roles,
    companies: user.companies,
    activeCompany: preferredCompany ?? user.profile.mainCompany,
  };
}

function isInRole(state: StoreState, role: UserRole) {
  return state.user.roles.includes(role);
}

async function getCurrentUser() {
  const user = await axios.get<UserMeResponse>("/api/user/me");

  return user.data;
}

export function useCompanySelector() {
  const store = useAuthenticatedUserStore();

  // The app reloads into the new company: on the start page, or wherever `landOn` points.
  const selectCompany = async (companyId: string | null, landOn = "/") => {
    const previousCompanyId = store.user.activeCompany?.id;
    if (store.user.isAuthenticated) {
      store.user.activeCompany =
        store.user.companies.find((company) => company.id === companyId) ?? store.user.profile.mainCompany;
    } else {
      store.user.activeCompany = null;
    }

    if (
      companyId !== null &&
      companyId === store.user.activeCompany?.id &&
      store.user.activeCompany?.id !== store.user.profile?.mainCompany.id
    ) {
      sessionStorage.setItem(CompanyIdLocalStorageKey, companyId);
    } else {
      sessionStorage.removeItem(CompanyIdLocalStorageKey);
    }
    // Said after the reload (see takeCompanySwitchNotice): nothing on the page survives it.
    if (store.user.activeCompany && store.user.activeCompany.id !== previousCompanyId) {
      sessionStorage.setItem(CompanySwitchNoticeKey, store.user.activeCompany.name);
    }
    window.location.href = landOn;
  };

  return { selectCompany };
}

const CompanyIdLocalStorageKey = "company-id";
const CompanySwitchNoticeKey = "company-switched-to";

/** The company just switched to, once: the first call after the reload returns it, later ones null. */
export function takeCompanySwitchNotice(): string | null {
  const companyName = sessionStorage.getItem(CompanySwitchNoticeKey);
  sessionStorage.removeItem(CompanySwitchNoticeKey);
  return companyName;
}

function getPreferredCompanyId() {
  return sessionStorage.getItem(CompanyIdLocalStorageKey);
}
